import { useState, useEffect } from 'react';
import { useApi } from '@/hooks/useApi';
import { useToast } from '@/hooks/use-toast';
import { DeliveryZone, DeliveryZoneUpdateData } from '@/types/delivery-zone.types';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Loader2 } from 'lucide-react';

interface EditDeliveryZoneModalProps {
  open: boolean;
  zone: DeliveryZone | null;
  onClose: () => void;
  onSuccess: () => void;
}

export function EditDeliveryZoneModal({
  open,
  zone,
  onClose,
  onSuccess,
}: EditDeliveryZoneModalProps) {
  const { patch, loading } = useApi();
  const { toast } = useToast();
  
  const [formData, setFormData] = useState<DeliveryZoneUpdateData>({
    code: '',
    name: '',
    description: '',
    is_active: true,
  });

  useEffect(() => {
    if (zone) {
      setFormData({
        code: zone.code,
        name: zone.name,
        description: zone.description || '',
        is_active: zone.is_active,
      });
    }
  }, [zone]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!zone) return;

    if (!formData.code || !formData.name) {
      toast({
        title: 'خطأ',
        description: 'يرجى ملء جميع الحقول المطلوبة',
        variant: 'destructive',
      });
      return;
    }

    try {
      await patch(`/api/v1/settings/delivery-zones/${zone.id}`, formData);
      toast({
        title: 'تم التحديث',
        description: 'تم تحديث منطقة التوصيل بنجاح',
      });
      onSuccess();
      onClose();
    } catch (error) {
      toast({
        title: 'خطأ',
        description: 'فشل في تحديث منطقة التوصيل',
        variant: 'destructive',
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" dir="rtl">
        <DialogHeader>
          <DialogTitle>تعديل منطقة التوصيل</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="edit-code">رمز المنطقة</Label>
            <Input
              id="edit-code"
              value={formData.code}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, code: e.target.value }))
              }
              className="text-end"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-name">اسم المنطقة</Label>
            <Input
              id="edit-name"
              value={formData.name}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, name: e.target.value }))
              }
              className="text-end"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-description">الوصف</Label>
            <Textarea
              id="edit-description"
              value={formData.description}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, description: e.target.value }))
              }
              className="text-end resize-none"
              rows={3}
            />
          </div>

          <div className="flex items-center justify-between">
            <Label htmlFor="is-active">نشط</Label>
            <Switch
              id="is-active"
              checked={formData.is_active}
              onCheckedChange={(checked) =>
                setFormData((prev) => ({ ...prev, is_active: checked }))
              }
            />
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={onClose}>
              إلغاء
            </Button>
            <Button type="submit" disabled={loading}>
              {loading && <Loader2 className="h-4 w-4 ms-2 animate-spin" />}
              حفظ التغييرات
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
